import { useState } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { AlertCircle, ChevronRight, ChevronDown, Layers } from 'lucide-react'
import type { UnitGroup, DisplaySubRow, RevenueStreamId, YieldComparison } from '@/lib/reports/space-yield/types'
import { formatCurrency, formatYieldPerSqft } from '@/lib/reports/space-yield/compute'

// ─── Types ──────────────────────────────────────────────────────────

interface BuildingBarsProps {
  groups: UnitGroup[]
  onGroupClick?: (groupKey: string) => void
  streams?: RevenueStreamId[]
}

type WarningType = 'underpriced' | 'sales_gap' | 'low_occupancy'

interface WarningFlag {
  type: WarningType
  label: string
}

// ─── Warning Detection ──────────────────────────────────────────────

function detectWarning(group: UnitGroup, facilityAvgYield: number): WarningFlag | null {
  const occ = group.occupancy_pct
  const yield_ = group.avg_yield

  // underpriced: high occupancy + low yield relative to facility avg
  if (occ >= 80 && facilityAvgYield > 0 && yield_ < facilityAvgYield * 0.8) {
    return { type: 'underpriced', label: 'Underpriced — high occupancy but yield below facility average' }
  }

  // sales_gap: good yield but low occupancy
  if (yield_ >= facilityAvgYield && occ < 60) {
    return { type: 'sales_gap', label: 'Sales gap — competitive pricing but low occupancy' }
  }

  // low_occupancy: just low occupancy
  if (occ < 60) {
    return { type: 'low_occupancy', label: 'Low occupancy — below 60%' }
  }

  return null
}

// ─── Mini Bar (Desktop) ─────────────────────────────────────────────

function MiniBar({
  pct,
  colorClass,
  bgClass,
  label,
  tooltipContent,
}: {
  pct: number
  colorClass: string
  bgClass: string
  label: React.ReactNode
  tooltipContent: React.ReactNode
}) {
  const clampedPct = Math.max(0, Math.min(100, pct))

  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className={`h-2.5 w-full rounded-full overflow-hidden ${bgClass}`}>
            <div
              className={`h-full rounded-full transition-all ${colorClass}`}
              style={{ width: `${clampedPct}%` }}
            />
          </div>
          <span className="text-xs text-slate-500 truncate">{label}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent className="text-xs max-w-xs">{tooltipContent}</TooltipContent>
    </Tooltip>
  )
}

// ─── Mobile Mini Bar ────────────────────────────────────────────────

function MobileMiniBar({
  pct,
  colorClass,
  bgClass,
  label,
  header,
}: {
  pct: number
  colorClass: string
  bgClass: string
  label: React.ReactNode
  header: string
}) {
  const clampedPct = Math.max(0, Math.min(100, pct))

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{header}</span>
      <div className={`h-2 w-full rounded-full overflow-hidden ${bgClass}`}>
        <div
          className={`h-full rounded-full ${colorClass}`}
          style={{ width: `${clampedPct}%` }}
        />
      </div>
      <span className="text-xs text-slate-500">{label}</span>
    </div>
  )
}

// ─── Segmented Revenue Bar ──────────────────────────────────────────

function SegmentedRevenueBar({
  group,
  maxRevenue,
  streams,
}: {
  group: UnitGroup
  maxRevenue: number
  streams?: RevenueStreamId[]
}) {
  const showLease = !streams || streams.includes('lease')
  const showTrans = !streams || streams.includes('transient')
  const showFuel  = !streams || streams.includes('fuel')

  const leaseVal = showLease ? group.lease_revenue : 0
  const transVal = showTrans ? group.transient_revenue : 0
  const fuelVal  = showFuel  ? group.fuel_revenue : 0
  const displayTotal = leaseVal + transVal + fuelVal

  const barPct = Math.max(0, Math.min(100, maxRevenue > 0 ? (displayTotal / maxRevenue) * 100 : 0))

  // Segment widths relative to bar fill
  const leaseW = displayTotal > 0 ? (leaseVal / displayTotal) * 100 : 0
  const transW = displayTotal > 0 ? (transVal / displayTotal) * 100 : 0
  const fuelW  = displayTotal > 0 ? (fuelVal / displayTotal) * 100 : 0

  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="h-2.5 w-full rounded-full overflow-hidden bg-slate-100">
            <div
              className="h-full flex rounded-full overflow-hidden transition-all"
              style={{ width: `${barPct}%` }}
            >
              {leaseW > 0 && (
                <div className="h-full bg-emerald-500" style={{ width: `${leaseW}%` }} />
              )}
              {transW > 0 && (
                <div className="h-full bg-blue-500" style={{ width: `${transW}%` }} />
              )}
              {fuelW > 0 && (
                <div className="h-full bg-amber-500" style={{ width: `${fuelW}%` }} />
              )}
            </div>
          </div>
          <span className="text-xs truncate">
            <span className="font-medium text-slate-700">{formatCurrency(displayTotal)}</span>
            {(() => {
              const parts: string[] = []
              if (showLease && leaseVal > 0) parts.push(`L: ${formatCurrency(leaseVal)}`)
              if (showTrans && transVal > 0) parts.push(`T: ${formatCurrency(transVal)}`)
              if (showFuel && fuelVal > 0) parts.push(`F: ${formatCurrency(fuelVal)}`)
              return parts.length > 1
                ? <span className="text-slate-400 ml-1">({parts.join(' · ')})</span>
                : null
            })()}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent className="text-xs max-w-xs">
        <RevenueTooltip group={group} streams={streams} />
      </TooltipContent>
    </Tooltip>
  )
}

// ─── Warning Badge ──────────────────────────────────────────────────

function WarningBadge({ warning }: { warning: WarningFlag }) {
  const colorClass =
    warning.type === 'underpriced' ? 'text-rose-500' : 'text-amber-500'

  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <AlertCircle className={`w-4 h-4 ${colorClass} shrink-0`} />
      </TooltipTrigger>
      <TooltipContent className="text-xs max-w-xs">{warning.label}</TooltipContent>
    </Tooltip>
  )
}

// ─── Tooltip Content Components ─────────────────────────────────────

function RevenueTooltip({ group, streams }: { group: UnitGroup; streams?: RevenueStreamId[] }) {
  const showLease = !streams || streams.includes('lease')
  const showTrans = !streams || streams.includes('transient')
  const showFuel  = !streams || streams.includes('fuel')
  const hasFuel = showFuel && group.fuel_revenue > 0

  return (
    <div className="space-y-1">
      <p className="font-semibold">{group.label} — Revenue</p>
      {showLease && <p>Lease: {formatCurrency(group.lease_revenue)}</p>}
      {showTrans && <p>Transient: {formatCurrency(group.transient_revenue)}</p>}
      {hasFuel && (
        <p className="text-amber-400">Fuel: {formatCurrency(group.fuel_revenue)}</p>
      )}
      <p className="text-slate-400 mt-1 border-t border-slate-600 pt-1">
        {group.total_sqft.toLocaleString()} sqft across {group.total_units} units
      </p>
    </div>
  )
}

function OccupancyTooltip({ group }: { group: UnitGroup }) {
  const idleSqft = group.total_sqft - group.occupied_sqft

  return (
    <div className="space-y-1">
      <p className="font-semibold">{group.label} — Occupancy</p>
      <p>{group.occupancy_pct}% occupied</p>
      <p>Occupied: {group.occupied_sqft.toLocaleString()} sqft</p>
      <p>Idle: {idleSqft.toLocaleString()} sqft ({group.idle_count} units)</p>
    </div>
  )
}

function YieldTooltip({
  group,
  facilityAvgYield,
}: {
  group: UnitGroup
  facilityAvgYield: number
}) {
  const delta = facilityAvgYield > 0
    ? ((group.avg_yield - facilityAvgYield) / facilityAvgYield) * 100
    : 0
  const deltaSign = delta >= 0 ? '+' : ''

  return (
    <div className="space-y-1">
      <p className="font-semibold">{group.label} — Revenue/Sqft</p>
      <p>Building: {formatYieldPerSqft(group.avg_yield)}/sqft</p>
      <p>Facility avg: {formatYieldPerSqft(facilityAvgYield)}/sqft</p>
      {facilityAvgYield > 0 && (
        <p className={delta >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
          {deltaSign}{delta.toFixed(0)}% vs facility
        </p>
      )}
    </div>
  )
}

// ─── Expandable Client Rows ─────────────────────────────────────────

/** Check if a group has expandable lease/transient sub-rows */
function hasExpandableRows(group: UnitGroup): boolean {
  return group.sub_rows.some(
    (r) => r.kind === 'lease' || r.kind === 'transient_agg',
  )
}

/** Format lease type as a short badge label */
function leaseTypeBadge(type: string): { label: string; className: string } {
  switch (type) {
    case 'month_to_month':
      return { label: 'M2M', className: 'bg-amber-100 text-amber-700' }
    case 'seasonal':
      return { label: 'Seasonal', className: 'bg-sky-100 text-sky-700' }
    default:
      return { label: 'Long-term', className: 'bg-emerald-100 text-emerald-700' }
  }
}

/** Render the expanded client rows for a building group */
function ExpandedClientRows({ subRows }: { subRows: DisplaySubRow[] }) {
  // Filter to only client-relevant rows (leases + transient aggregates)
  const clientRows = subRows.filter(
    (r): r is Extract<DisplaySubRow, { kind: 'lease' | 'transient_agg' }> =>
      r.kind === 'lease' || r.kind === 'transient_agg',
  )

  if (clientRows.length === 0) return null

  return (
    <div className="pl-8 pr-2 pb-1 space-y-0.5">
      {clientRows.map((row, idx) => {
        const isLast = idx === clientRows.length - 1
        const connector = isLast ? '└' : '├'

        if (row.kind === 'lease') {
          const d = row.data
          const badge = leaseTypeBadge(d.lease_type)
          return (
            <div
              key={d.lease_id}
              className="flex items-center gap-3 text-xs text-slate-600 py-0.5"
            >
              <span className="text-slate-400 w-3 shrink-0">{connector}</span>
              <span className="truncate min-w-0 max-w-[180px]">{d.tenant_name}</span>
              <span className="font-medium tabular-nums">{formatCurrency(d.lease_rate)}/mo</span>
              {d.fuel_revenue != null && d.fuel_revenue > 0 && (
                <span className="text-amber-600 font-medium tabular-nums">+{formatCurrency(d.fuel_revenue)} fuel</span>
              )}
              {d.fuel_commitment_gallons != null && d.fuel_commitment_gallons > 0 && (
                <span className="text-amber-500 tabular-nums text-[11px]">
                  {d.fuel_delivered_gallons ?? 0}/{d.fuel_commitment_gallons} gal ({d.fuel_commitment_pct ?? 0}%)
                </span>
              )}
              <span className="text-slate-400">{d.contract_number}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${badge.className}`}>
                {badge.label}
              </span>
            </div>
          )
        }

        const d = row.data
        return (
          <div
            key={`trans-agg-${idx}`}
            className="flex items-center gap-3 text-xs text-slate-600 py-0.5"
          >
            <span className="text-slate-400 w-3 shrink-0">{connector}</span>
            <span className="truncate min-w-0 max-w-[180px]">
              {d.stay_count} {d.stay_count === 1 ? 'stay' : 'stays'}
            </span>
            <span className="font-medium tabular-nums">{formatCurrency(d.total_revenue)}</span>
            <span className="text-slate-400">{d.utilization_pct}% util</span>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700">
              Transient
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ─── Expanded Yield Bars ─────────────────────────────────────────────

const YIELD_SEGMENTS: { key: keyof YieldComparison; label: string; dotClass: string; barClass: string }[] = [
  { key: 'lease',     label: 'Lease',     dotClass: 'bg-emerald-500', barClass: 'bg-emerald-500' },
  { key: 'transient', label: 'Transient', dotClass: 'bg-blue-500',    barClass: 'bg-blue-500' },
  { key: 'combined',  label: 'Combined',  dotClass: 'bg-slate-500',   barClass: 'bg-slate-500' },
]

function ExpandedYieldBars({
  yieldComparison,
  maxYield,
}: {
  yieldComparison: YieldComparison
  maxYield: number
}) {
  const rows = YIELD_SEGMENTS.filter((s) => yieldComparison[s.key].sqft > 0)
  if (rows.length === 0) return null

  return (
    <div className="space-y-1 py-1">
      {rows.map((seg) => {
        const data = yieldComparison[seg.key]
        const total = data.space_yield + data.fuel_yield
        const barPct = maxYield > 0 ? Math.min(100, (total / maxYield) * 100) : 0
        const spaceW = total > 0 ? (data.space_yield / total) * 100 : 0
        const fuelW = total > 0 ? (data.fuel_yield / total) * 100 : 0

        return (
          <Tooltip key={seg.key} delayDuration={100}>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full shrink-0 ${seg.dotClass}`} />
                <span className="text-[10px] text-slate-500 w-16 shrink-0">{seg.label}</span>
                <div className="flex-1 h-2 rounded-full overflow-hidden bg-slate-100">
                  <div
                    className="h-full flex rounded-full overflow-hidden"
                    style={{ width: `${barPct}%` }}
                  >
                    {spaceW > 0 && (
                      <div className={`h-full ${seg.barClass}`} style={{ width: `${spaceW}%` }} />
                    )}
                    {fuelW > 0 && (
                      <div className="h-full bg-amber-500" style={{ width: `${fuelW}%` }} />
                    )}
                  </div>
                </div>
                <span className="text-xs font-medium text-slate-700 tabular-nums w-14 text-right shrink-0">
                  {formatYieldPerSqft(total)}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="text-xs max-w-xs">
              <div className="space-y-0.5">
                <p className="font-semibold">{seg.label} Yield</p>
                <p>Space: {formatYieldPerSqft(data.space_yield)}/sqft</p>
                {data.fuel_yield > 0 && (
                  <p className="text-amber-400">Fuel: {formatYieldPerSqft(data.fuel_yield)}/sqft</p>
                )}
                {seg.key === 'transient' && data.avg_rate_per_night != null && (
                  <p className="text-blue-400">
                    Avg {formatCurrency(data.avg_rate_per_night)}/night · {data.occupied_nights ?? 0} nights · {data.utilization_pct ?? 0}% util
                  </p>
                )}
                <p className="text-slate-400">{data.sqft.toLocaleString()} sqft</p>
              </div>
            </TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
}

// ─── Main Component ─────────────────────────────────────────────────

export function BuildingBars({ groups, onGroupClick, streams }: BuildingBarsProps) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [yieldExpandedGroups, setYieldExpandedGroups] = useState<Record<string, boolean>>({})

  if (groups.length === 0) return null

  const toggleExpand = (key: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const toggleYieldExpand = (key: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setYieldExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  // Compute scaling references based on enabled streams
  const showLease = !streams || streams.includes('lease')
  const showTrans = !streams || streams.includes('transient')
  const showFuel  = !streams || streams.includes('fuel')

  const maxRevenue = Math.max(
    ...groups.map((g) => {
      let total = 0
      if (showLease) total += g.lease_revenue
      if (showTrans) total += g.transient_revenue
      if (showFuel) total += g.fuel_revenue
      return total
    }),
    1,
  )
  // Include per-segment yield totals so expanded bars fit within scale
  const maxYield = Math.max(
    ...groups.flatMap((g) => {
      const yc = g.yield_comparison
      return [
        g.avg_yield,
        yc.lease.space_yield + yc.lease.fuel_yield,
        yc.transient.space_yield + yc.transient.fuel_yield,
        yc.combined.space_yield + yc.combined.fuel_yield,
      ]
    }),
    0.01,
  )

  const totalRev = groups.reduce((s, g) => s + g.total_revenue, 0)
  const totalSqft = groups.reduce((s, g) => s + g.total_sqft, 0)
  const facilityAvgYield = totalSqft > 0 ? totalRev / totalSqft : 0

  return (
    <div className="bg-white p-6 rounded-lg border shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900 mb-4">Building Performance</h3>

      {/* Desktop layout */}
      <div className="hidden md:block">
        {/* Column headers */}
        <div className="grid grid-cols-[180px_1fr_1fr_1fr_28px] gap-4 mb-2 px-1">
          <span />
          <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Revenue</span>
          <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Occupancy</span>
          <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Revenue/Sqft</span>
          <span />
        </div>

        {/* Building rows */}
        <div className="space-y-1">
          {groups.map((group) => {
            const occPct = group.occupancy_pct
            const yieldPct = (group.avg_yield / maxYield) * 100
            const warning = detectWarning(group, facilityAvgYield)
            const expandable = hasExpandableRows(group)
            const isExpanded = !!expandedGroups[group.key]

            return (
              <div key={group.key}>
                <div
                  className="grid grid-cols-[180px_1fr_1fr_1fr_28px] gap-4 items-center px-1 py-1 rounded-md hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => onGroupClick?.(group.key)}
                >
                  {/* Building name with chevron */}
                  <div className="flex items-center gap-1 min-w-0">
                    {expandable ? (
                      <button
                        onClick={(e) => toggleExpand(group.key, e)}
                        className="shrink-0 p-0.5 rounded hover:bg-slate-200 transition-colors"
                      >
                        {isExpanded
                          ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                          : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                      </button>
                    ) : (
                      <span className="w-[18px] shrink-0" />
                    )}
                    <span className="text-sm font-medium text-slate-700 truncate">{group.label}</span>
                  </div>

                  {/* Revenue bar (segmented: lease/transient/fuel) */}
                  <SegmentedRevenueBar group={group} maxRevenue={maxRevenue} streams={streams} />

                  {/* Occupancy bar */}
                  <MiniBar
                    pct={occPct}
                    colorClass="bg-blue-500"
                    bgClass="bg-blue-100"
                    label={
                      <span>
                        <span className="font-medium text-slate-700">{occPct}%</span>
                        <span className="text-slate-400 ml-1">({group.occupied_sqft.toLocaleString()} / {group.total_sqft.toLocaleString()} sqft)</span>
                      </span>
                    }
                    tooltipContent={<OccupancyTooltip group={group} />}
                  />

                  {/* Yield bar — clickable to expand breakdown */}
                  <div
                    className="group/yield cursor-pointer relative"
                    onClick={(e) => toggleYieldExpand(group.key, e)}
                  >
                    <MiniBar
                      pct={yieldPct}
                      colorClass="bg-violet-500"
                      bgClass="bg-violet-100"
                      label={<span className="font-medium text-slate-700">{formatYieldPerSqft(group.avg_yield)}</span>}
                      tooltipContent={
                        <YieldTooltip group={group} facilityAvgYield={facilityAvgYield} />
                      }
                    />
                    <Layers className="w-3 h-3 text-slate-400 absolute top-0 right-0 opacity-0 group-hover/yield:opacity-100 transition-opacity" />
                  </div>

                  {/* Warning flag */}
                  <div className="flex items-center justify-center">
                    {warning && <WarningBadge warning={warning} />}
                  </div>
                </div>

                {/* Expanded yield breakdown */}
                {!!yieldExpandedGroups[group.key] && (
                  <div className="grid grid-cols-[180px_1fr_1fr_1fr_28px] gap-4 px-1">
                    <span />
                    <span />
                    <span />
                    <ExpandedYieldBars
                      yieldComparison={group.yield_comparison}
                      maxYield={maxYield}
                    />
                    <span />
                  </div>
                )}

                {/* Expanded client rows */}
                {isExpanded && expandable && (
                  <ExpandedClientRows subRows={group.sub_rows} />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Mobile layout — stacked vertical */}
      <div className="md:hidden space-y-4">
        {groups.map((group) => {
          const occPct = group.occupancy_pct
          const yieldPct = (group.avg_yield / maxYield) * 100
          const warning = detectWarning(group, facilityAvgYield)
          const expandable = hasExpandableRows(group)
          const isExpanded = !!expandedGroups[group.key]

          return (
            <div
              key={group.key}
              className="border rounded-md p-3 space-y-2 active:bg-slate-50 cursor-pointer"
              onClick={() => onGroupClick?.(group.key)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 min-w-0">
                  {expandable ? (
                    <button
                      onClick={(e) => toggleExpand(group.key, e)}
                      className="shrink-0 p-0.5 rounded hover:bg-slate-200 transition-colors"
                    >
                      {isExpanded
                        ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                        : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                    </button>
                  ) : (
                    <span className="w-[18px] shrink-0" />
                  )}
                  <span className="text-sm font-medium text-slate-700 truncate">{group.label}</span>
                </div>
                {warning && <WarningBadge warning={warning} />}
              </div>

              {/* Revenue (segmented) */}
              <div className="flex flex-col gap-0.5">
                <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Revenue</span>
                <SegmentedRevenueBar group={group} maxRevenue={maxRevenue} streams={streams} />
              </div>

              <MobileMiniBar
                pct={occPct}
                colorClass="bg-blue-500"
                bgClass="bg-blue-100"
                label={
                  <span>
                    <span className="font-medium text-slate-700">{occPct}%</span>
                    <span className="text-slate-400 ml-1">({group.occupied_sqft.toLocaleString()} / {group.total_sqft.toLocaleString()} sqft)</span>
                  </span>
                }
                header="Occupancy"
              />

              {/* Yield — tappable to expand breakdown */}
              <div
                className="relative"
                onClick={(e) => toggleYieldExpand(group.key, e)}
              >
                <MobileMiniBar
                  pct={yieldPct}
                  colorClass="bg-violet-500"
                  bgClass="bg-violet-100"
                  label={<span className="font-medium text-slate-700">{formatYieldPerSqft(group.avg_yield)}</span>}
                  header="Revenue/Sqft"
                />
                <Layers className="w-3 h-3 text-slate-400 absolute top-0 right-0" />
              </div>

              {/* Expanded yield breakdown (mobile) */}
              {!!yieldExpandedGroups[group.key] && (
                <div className="border-l-2 border-slate-200 pl-3 ml-1">
                  <ExpandedYieldBars
                    yieldComparison={group.yield_comparison}
                    maxYield={maxYield}
                  />
                </div>
              )}

              {/* Expanded client rows */}
              {isExpanded && expandable && (
                <ExpandedClientRows subRows={group.sub_rows} />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
